import {announce} from '@github-ui/aria-live'
import type {CopilotSpace, CopilotSpacePayload, IndexCopilotSpace} from '@github-ui/copilot-chat-types'
import {COPILOT_SPACES_QUERY_KEY, fetchCopilotSpace, fetchCopilotSpaces} from '@github-ui/copilot-spaces/utils/fetch'
import type {AhpSpaceSelection} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import {useQuery} from '@github-ui/react-query'
import {SpaceIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {ActionList, ActionMenu, Flash, Spinner} from '@primer/react'
import {use, useCallback, useEffect, useState} from 'react'

interface SpacePickerProps {
  selectedSpace: AhpSpaceSelection | undefined
  onSelectSpace: (space: AhpSpaceSelection | undefined) => void
  isResolving: boolean
  onResolvingChange: (isResolving: boolean) => void
}

const NONE_KEY = 'none'

function spaceKey(space: Pick<IndexCopilotSpace, 'owner' | 'id'>): string {
  return `${space.owner}/${space.id}`
}

function isSpacePayload(payload: CopilotSpacePayload): payload is CopilotSpace {
  return 'generalInstructions' in payload
}

export function SpacePicker({selectedSpace, onSelectSpace, isResolving, onResolvingChange}: SpacePickerProps) {
  const {afterSelect: closeAttachMenu} = use(ActionList.ContainerContext)
  const [isOpen, setIsOpen] = useState(false)
  const [selectionError, setSelectionError] = useState<string>()
  const {
    data: spaces,
    isLoading,
    isError,
  } = useQuery<IndexCopilotSpace[], Error>({
    queryKey: COPILOT_SPACES_QUERY_KEY,
    queryFn: () => fetchCopilotSpaces(),
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  })

  useEffect(() => {
    if (isError) announce('Could not load spaces', {assertive: true})
  }, [isError])

  const handleSelect = async (space: IndexCopilotSpace) => {
    if (isResolving) return
    setSelectionError(undefined)
    onResolvingChange(true)
    announce(`Resolving space: ${space.name}`)
    try {
      const result = await fetchCopilotSpace({owner: space.owner, id: space.id})
      const payload = result.ok && isSpacePayload(result.payload) ? result.payload : undefined

      if (!payload?.slug) {
        const message =
          result.ok && 'protectedOrganizations' in result.payload
            ? 'Authorize SSO for this space, then try again.'
            : 'Could not select this space. Try again.'
        setSelectionError(message)
        announce(message, {assertive: true})
        return
      }

      onSelectSpace({owner: space.owner, id: space.id, slug: payload.slug, name: space.name})
      announce(`Space selected: ${space.name}`)
      setIsOpen(false)
      closeAttachMenu?.()
    } catch {
      const message = 'Could not select this space. Try again.'
      setSelectionError(message)
      announce(message, {assertive: true})
    } finally {
      onResolvingChange(false)
    }
  }

  const handleClear = useCallback(() => {
    if (isResolving) return
    setSelectionError(undefined)
    onSelectSpace(undefined)
  }, [isResolving, onSelectSpace])

  const selectedKey = selectedSpace ? spaceKey(selectedSpace) : NONE_KEY

  return (
    <ActionMenu
      open={isOpen}
      onOpenChange={next => {
        if (!next || !isResolving) setIsOpen(next)
      }}
    >
      <ActionMenu.Anchor>
        <ActionList.Item disabled={isResolving}>
          <ActionList.LeadingVisual>
            {isResolving ? <Spinner size="small" srText={null} /> : <SpaceIcon />}
          </ActionList.LeadingVisual>
          {`Space: ${isResolving ? 'Resolving…' : (selectedSpace?.name ?? 'No space')}`}
        </ActionList.Item>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium" maxHeight="medium" overflow="auto" side="outside-right" align="end">
        {selectionError && <Flash variant="danger">{selectionError}</Flash>}
        <ActionList selectionVariant="single">
          <ActionList.Item selected={selectedKey === NONE_KEY} disabled={isResolving} onSelect={handleClear}>
            No space
          </ActionList.Item>
          <ActionList.Divider />
          {isLoading ? (
            <ActionList.Item disabled>Loading spaces…</ActionList.Item>
          ) : isError ? (
            <ActionList.Item disabled>Could not load spaces</ActionList.Item>
          ) : spaces && spaces.length > 0 ? (
            spaces.map(space => (
              <ActionList.Item
                key={spaceKey(space)}
                selected={selectedKey === spaceKey(space)}
                disabled={isResolving}
                onSelect={event => {
                  event.preventDefault()
                  void handleSelect(space)
                }}
              >
                {space.name}
                <ActionList.Description>{space.owner}</ActionList.Description>
              </ActionList.Item>
            ))
          ) : (
            <ActionList.Item disabled>No spaces available</ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
